import type { ReactNode } from "react"
import { useMemo } from "react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import {
	categoricalColor,
	reportingBaselineClassName,
	ReportingChartFrame,
	reportingChartTheme,
	ReportingChartTooltip,
	reportingGridClassName,
} from "@/components/reporting/charts"
import { useReportingBarHoverCursor } from "@/components/reporting/charts/use-dark-mode"
import {
	formatDayLabel,
	formatDayTick,
	formatWeekLabel,
	formatWeekRangeLabel,
} from "./week-label"

type ChartDatum = Record<string, string | number | Date>

interface WorkspaceStackedBarChartProps {
	title: string
	description?: string
	data: ChartDatum[] | undefined
	isLoading: boolean
	// Bars can carry money as well as counts, so the axis and the tooltip share
	// one formatter.
	formatValue?: (value: number) => string
	legend?: ReactNode
	className?: string
	height?: string
	unit?: "day" | "week"
}

export function WorkspaceStackedBarChart({
	title,
	description,
	data,
	isLoading,
	formatValue,
	legend,
	className,
	height = "h-[200px]",
	unit = "week",
}: WorkspaceStackedBarChartProps) {
	const hoverCursor = useReportingBarHoverCursor()

	const { chartData, workspaceNames } = useMemo(() => {
		if (!data) return { chartData: [] as ChartDatum[], workspaceNames: [] }

		const totals = new Map<string, number>()
		data.forEach((item) => {
			Object.entries(item).forEach(([key, value]) => {
				if (key === "bucket" || key === "createdAt") return
				if (typeof value !== "number") return
				totals.set(key, (totals.get(key) ?? 0) + value)
			})
		})

		const names = [...totals.entries()]
			.sort((a, b) => b[1] - a[1])
			.map(([name]) => name)

		const transformed = data.map((item) => ({
			...item,
			date:
				unit === "day"
					? formatDayTick(String(item.bucket))
					: formatWeekLabel(String(item.bucket)),
			rangeLabel:
				unit === "day"
					? formatDayLabel(String(item.bucket))
					: formatWeekRangeLabel(String(item.bucket)),
		}))

		return { chartData: transformed, workspaceNames: names }
	}, [data, unit])

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={chartData.length === 0}
			legend={legend}
			className={className}
		>
			<div className={`${height} w-full [&_*]:outline-none`}>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
					>
						<CartesianGrid
							className={reportingGridClassName}
							vertical={false}
						/>
						<ReferenceLine y={0} className={reportingBaselineClassName} />
						<XAxis
							dataKey="date"
							tickLine={false}
							axisLine={false}
							interval="preserveStartEnd"
							tick={{ fontSize: 11 }}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<YAxis
							orientation="right"
							tickLine={false}
							axisLine={false}
							className="text-xs text-gray-500 dark:text-gray-400"
							width={formatValue ? 44 : 32}
							tickFormatter={
								formatValue ? (value: number) => formatValue(value) : undefined
							}
						/>
						<Tooltip
							cursor={hoverCursor}
							content={
								<ReportingChartTooltip
									hideZero
									formatter={(value, name) => [
										formatValue ? formatValue(value) : String(value),
										name,
									]}
								/>
							}
						/>
						{workspaceNames.map((workspace, index) => (
							<Bar
								key={workspace}
								dataKey={workspace}
								name={workspace}
								stackId="stack"
								fill={categoricalColor(index)}
								maxBarSize={reportingChartTheme.maxBarSize}
								radius={
									index === workspaceNames.length - 1
										? [
												reportingChartTheme.barRadius,
												reportingChartTheme.barRadius,
												0,
												0,
											]
										: 0
								}
								isAnimationActive={false}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>
		</ReportingChartFrame>
	)
}
